import {
  Accordion,
  Button,
  Carousel,
  Icon,
  PostBookingCard,
  PostBookingCardSkeleton,
  Text,
  TooltipNew,
} from '@msc-b2c/shared-components';
import { useProductCarouselContext } from './ProductCarousel.hooks';
import {
  CartItem,
  DiningSelectionMetadata,
  OBSObjI,
  PillConfigurations,
} from '@msc-b2c/interfaces/types';
import {
  GetShopExcursionMyMSCResponse,
  MyMscExcursion,
  OBSI,
  PackageDetails,
  ShopOBSPassengerI,
} from '@msc-b2c/interfaces/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  breakpoints,
  cartProductCarouselAdded,
  decodeHtmlEntities,
  getActBaseImagePath,
  getActiveSession,
  getActPlaceholderImagePath,
  getAvailablePrice,
  getCurrencyCode,
  getDynamicMediaPath,
  getObsBaseImagePath,
  getObsPlaceholderImagePath,
  isLayerMyAreaExcursionOpen,
  managePlurals,
  myAreaShopExcursion,
  myAreaShopOBS,
  replacePlaceholders,
  RootState,
  selectedMyAreaExcursionCard,
  selectedMyAreaOBSCard,
  setErrorManagerCode,
  setRelatedObsTitlesMap,
  toggleDisplayOverlay,
  updateCartItems,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  getAlreadyBookedPassengerId,
  getDisplayButton,
  getNamesOfAlreadyBookedPassengers,
  getPostBookingCardPrices,
  getPriceFromPriceBasis,
  isItemNotFullyBooked,
  isNoEligiblePassengersForExcursion,
  isNoEligiblePassengersForOBS,
  isPriceTypeNotAllowed,
  isReservationAvailable,
} from '../postbookingresults/utils/PostBookingResultsUtilities';
import {
  getLinkedMinorObs,
  isHiddenMinorObs,
} from '../postbookingresults/utils/allInclusiveCmsHelpers';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildExcursionShopRequestKey,
  buildObsShopRequestKey,
  getAllShopOBSData,
  getCabinIdOfTheLoggedUser,
  getShopExcursionData,
  getShopOBSData,
} from '../utils/myAreaUtils';
import { createRequestGuard } from '../utils/requestGuardUtils';
import Overview from '../postbookingresults/components/drawercontent-subcomponents/Overview';
import Passengers from '../postbookingresults/components/drawercontent-subcomponents/Passengers';
import ImageTitle from '../postbookingresults/components/drawercontent-subcomponents/ImageTitle';
import {
  triggerAddToCartEvent,
  triggerViewItemEvent,
  triggerViewItemList,
} from '../utils/myAreaAnalytics';
import { filterPills } from '../utils/pillManagementUtils';
import { extractTooltipContent } from '../postbookingresults/utils/TooltipUtilities';
import { Drawer } from '../drawer/Drawer';
import AddToCartOverview from '../postbookingresults/components/drawercontent-subcomponents/AddToCartOverview';
import { navigateOrRedirectToRoute } from '../navigationmenu/utils/navigationMenuUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorModal } from '../errormodal/ErrorModal';
import {
  addAllIncItemAndRelatedItemToCart,
  addDiningItemToCart,
  addToCart,
  hasAllInclusiveOrCombinedInBooking,
  hasAllInclusiveOrCombinedInCart,
} from '../utils/myAreaCartUtils';
import {
  DiningFlowEntryPoint,
  ErrorManagerCodeEnum,
  OBSTypesEnum,
} from '@msc-b2c/interfaces/enums';
import { DiningMultistepDrawer } from '../postbookingresults/components/drawercontent-subcomponents/flowtypes/DiningMultistepDrawer';
import styles from './productcarousel.module.scss';
import { buildProductCarouPoolData } from './helpers/itemCodeMatcher';
import { defineItemListName } from '../utils/analyticsUtils';

export type AllTypes = MyMscExcursion | OBSObjI;
export type CarouselCardData = {
  image: string;
  imagePlaceholder: string;
  imageCode?: string;
  imageType?: 'obs' | 'act';
  text: string;
  price: string;
  bookedPassengers: string;
  code: string;
  day?: number;
  portCode?: string;
  startDate?: string;
  dayAtSeaLabelValue?: string;
  durationLabel?: string;
  packageTitle?: string;
  priceLabel?: string;
  isAllIncItem?: boolean;
};
type ViewItemRequestData = {
  carousel_name?: string;
  promoBadges?: PillConfigurations[];
  itemCategory2?: string;
};

// Persists across carousel remounts to bypass triggerViewItemList's duplicate guard in some cases.
let productCarouselVisitCounter = 0;

export function ProductCarousel({
  preTitle,
  title,
  subtitle,
  dataComponentName,
}: Readonly<{
  preTitle: string;
  title: string;
  subtitle: string;
  dataComponentName: string;
}>) {
  const cabinDetails = useSelector((state: RootState) => state.myCruise.cabinDetails);
  const { productCarouselAEMProps, productCarouselData } = useProductCarouselContext();

  const serviceTypesItemCodes = productCarouselAEMProps.serviceTypeOrder;
  const carouseMaxItems = productCarouselAEMProps.carouselMaxItems;
  const postBookingButtonAriaLabel = productCarouselAEMProps.buttonAriaLabel;
  const isLowestPriceSortingEnabled = productCarouselAEMProps.enableLowestPriceLogic;

  // check if we are on cart page
  const carouselIsOnCartPage = !!document.querySelector('.cartcontainer');
  const hasAddedToCartRef = useRef(false); // flag to check if item is added to the cart

  const propertiesFromPlanMyCruiseContainer = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps
  );
  const myAreaExcursionCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const myAreaOBSCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const isLayerExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const { cardPillConfigurations } = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps
  );
  const myAreaPillsVisibility = useSelector(
    (state: RootState) => state.myAreaCommonConfig.pillsVisibility
  );
  const confirmationDrawerProps = useSelector(
    (state: RootState) => state.myAreaCommonConfig.confirmationDrawerProps
  );

  const drawerPrice = useSelector((state: RootState) => state.myAreaDrawers.drawerPrice);
  const drawerAEMConfig = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);
  const drawerExcursionAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig?.drawerExcursionProps?.drawerExcursionType
  );
  const { shopExcursion, shopOBS, membershipData } = useSelector(
    (state: RootState) => state.myCruise
  );
  const allOBS = useSelector((state: RootState) => state.myCruise.obs?.onboardServices);
  const allInclusiveCmsItems = useSelector(
    (state: RootState) => state.myCruise.allInclusiveCmsItems
  );
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedCardPills, setSelectedCardPills] = useState<PillConfigurations[]>([]);
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const cruiseExcursions = useSelector((state: RootState) => state.myCruise.excursions?.excursions);
  const cruiseObs = useSelector((state: RootState) => state.myCruise.obs?.onboardServices);
  const { alreadyPurchasedSectionRouteMapping } = useSelector(
    (state: RootState) => state.navigationMenuData
  );

  const [isMobile, setIsMobile] = useState<boolean>(globalThis.innerWidth <= breakpoints.md);

  const [triggerAddToCart, setTriggerAddToCart] = useState<boolean>(false);
  const [passengersToAdd, setPassengersToAdd] = useState<Record<
    string,
    Record<string, boolean>
  > | null>(null);
  const [diningSelectionToAdd, setDiningSelectionToAdd] = useState<DiningSelectionMetadata | null>(
    null
  );
  const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({});
  const [isConfirmationDrawer, setIsConfirmationDrawer] = useState(false);
  const [isDrawerProcessing, setIsDrawerProcessing] = useState<boolean>(false);
  const [allPassengersInCart, setAllPassengersInCart] = useState(false);
  const [isAllInclusiveInCartErrorModalOpen, setIsAllInclusiveInCartErrorModalOpen] =
    useState<boolean>(false);
  // Keep one guard per flow to avoid duplicate INT135 calls and to skip re-fetch
  // when the same card drawer is reopened consecutively.
  const obsRequestGuardRef = useRef(createRequestGuard<OBSI>());
  const excursionRequestGuardRef = useRef(createRequestGuard<GetShopExcursionMyMSCResponse>());
  const hasTriggeredViewItemListRef = useRef(false);
  const viewItemListPageUrlRef = useRef('');
  const viewItemListDataKeyRef = useRef('');
  const lastTriggeredViewItemListPageUrlRef = useRef('');
  const languageList = myAreaExcursionCardData?.activityDetails?.guideLanguageCodes ?? [];
  const buttonLabel = productCarouselAEMProps.buttonLabel;

  const {
    alreadyBookedLabel,
    durationLabel,
    codeLabel,
    perAdultLabel,
    perChildLabel,
    perCabinLabel,
    perPackageLabel,
    myPlanButtonLabel,
    myPlanButtonDiningLabel,
    dayLabel,
    dayAtSeaLabelValue,
    promotionPriceEnabled,
    showSinglePromotionPrice,
    myPlanAlreadyPurchasedButtonLabel,
    myPlanButtonPurchaseDisabledLabel,
    allInclusiveInCartErrorTitle,
    allInclusiveInCartErrorDescription,
    allInclusiveInCartErrorButtonText,
  } = propertiesFromPlanMyCruiseContainer;

  const shouldRenderAllInclusiveInCartErrorModal =
    Boolean(allInclusiveInCartErrorTitle) && Boolean(allInclusiveInCartErrorButtonText);

  const { bookingNumber = '' } = getActiveSession();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentPageUrl = `${location.pathname}`;

  const canAddToCartInPostBooking =
    ((isLayerExcursionOpen && myAreaExcursionCardData) || myAreaOBSCardData) &&
    passengersToAdd &&
    Object.values(passengersToAdd).some((passenger) =>
      Object.entries(passenger).some(([, isChecked]) => isChecked)
    ) &&
    (!isLayerExcursionOpen ||
      languageList.length <= 1 ||
      (languageList.length &&
        Object.values(passengersToAdd).every((passenger) =>
          Object.entries(passenger)
            .filter(([, isChecked]) => isChecked)
            .every(([passengerId]) => {
              const lang = selectedLanguages[passengerId];
              return typeof lang === 'string' && lang.trim() !== '';
            })
        )));

  const buildObsRelatedItemsMap = (obsList: OBSObjI[]): Record<string, string[]> => {
    const obsByCode = new Map(obsList.map((obs) => [obs.code, obs]));
    const relatedItemsMap: Record<string, string[]> = {};

    obsList.forEach((obs) => {
      const serviceCodes = obs.prepaidItems?.serviceCodes;
      if (!serviceCodes) return;

      const relatedCodes = [...(serviceCodes.adults ?? []), ...(serviceCodes.minors ?? [])].filter(
        (code) => code !== obs.code
      );

      if (!relatedCodes.length) return;

      const firstRelatedDescription = relatedCodes
        .map((code) => obsByCode.get(code)?.description ?? null)
        .find((desc): desc is string => Boolean(desc));

      if (firstRelatedDescription && obs.code) {
        relatedItemsMap[obs.code] = [firstRelatedDescription];
      }
    });

    return relatedItemsMap;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(globalThis.innerWidth <= breakpoints.md);

    globalThis.addEventListener('resize', handleResize);
    return () => globalThis.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!allOBS?.length) return;
    const relatedObs = buildObsRelatedItemsMap(allOBS);
    dispatch(setRelatedObsTitlesMap(relatedObs));
  }, [allOBS, dispatch]);

  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const activeCabinId = useMemo(() => {
    return getCabinIdOfTheLoggedUser(booking);
  }, [booking]);

  const bookingHasAllInclusiveOrCombined = useMemo(
    () => hasAllInclusiveOrCombinedInBooking(booking, allOBS),
    [booking, allOBS]
  );

  const cartHasAllInclusiveOrCombined = useMemo(
    () => hasAllInclusiveOrCombinedInCart(cartItems, allOBS),
    [cartItems, allOBS]
  );

  const handleShopRequestExc = (card: MyMscExcursion, viewItemData?: ViewItemRequestData) => {
    const requestKey = buildExcursionShopRequestKey(card);

    excursionRequestGuardRef.current.run(
      requestKey,
      () => getShopExcursionData(card),
      (res) => {
        dispatch(myAreaShopExcursion(res as never));
        if (!booking) return;
        triggerViewItemEvent(
          card,
          booking,
          { ...viewItemData, shopResponse: res },
          cabinDetails,
          membershipData
        );
      },
      () => {
        resetDrawerState();
        dispatch(setErrorManagerCode(ErrorManagerCodeEnum.NO_EXCURSION_MATCH));
      }
    );
  };

  const handleShopRequestOBS = (card: OBSObjI, viewItemData?: ViewItemRequestData) => {
    const requestKey = buildObsShopRequestKey(card);

    obsRequestGuardRef.current.run(
      requestKey,
      () => getShopOBSData(card),
      (res) => {
        dispatch(myAreaShopOBS(res));
        if (!booking) return;
        triggerViewItemEvent(
          card,
          booking,
          { ...viewItemData, shopResponse: res },
          cabinDetails,
          membershipData
        );
      }
    );
  };

  const handleToggle = (index: string) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const mergeOBSResults = (results: OBSI[], baseCard: OBSObjI): OBSI => {
    if (!results.length) return {} as OBSI;

    const base = { ...results[0] };
    base.prices = {
      total: { beforeDiscount: '0', afterDiscount: '0' },
      taxes: { beforeDiscount: '0', afterDiscount: '0' },
      perPassenger: [],
    };

    const passengerMap = new Map<string, ShopOBSPassengerI>();
    const parsePrice = (str?: string) => (str ? Number.parseFloat(str) : 0);

    results.forEach((obs) => {
      base.prices.total.beforeDiscount = (
        parsePrice(base.prices.total.beforeDiscount) + parsePrice(obs.prices.total.beforeDiscount)
      ).toString();
      base.prices.total.afterDiscount = (
        parsePrice(base.prices.total.afterDiscount) + parsePrice(obs.prices.total.afterDiscount)
      ).toString();

      base.prices.taxes.beforeDiscount = (
        parsePrice(base.prices.taxes.beforeDiscount) + parsePrice(obs.prices.taxes.beforeDiscount)
      ).toString();
      base.prices.taxes.afterDiscount = (
        parsePrice(base.prices.taxes.afterDiscount) + parsePrice(obs.prices.taxes.afterDiscount)
      ).toString();

      obs.prices.perPassenger.forEach((passenger) => {
        if (!passenger.id) return;

        if (!passengerMap.has(passenger.id)) {
          passengerMap.set(passenger.id, {
            ...passenger,
            ...(passenger.isApplicable && { relatedItemCode: obs.code }),
          });
        } else {
          const existing = passengerMap.get(passenger.id);
          if (!existing) return;

          existing.total = existing.total || {
            beforeDiscount: '0',
            afterDiscount: '0',
            beforePromotionAndDiscount: '0',
          };
          existing.total.beforeDiscount = (
            parsePrice(existing.total.beforeDiscount) + parsePrice(passenger.total?.beforeDiscount)
          ).toString();
          existing.total.afterDiscount = (
            parsePrice(existing.total.afterDiscount) + parsePrice(passenger.total?.afterDiscount)
          ).toString();

          const existingBeforePromo = existing.total.beforePromotionAndDiscount || '0';
          const passengerBeforePromo =
            passenger.total?.beforePromotionAndDiscount ?? passenger.total?.beforeDiscount;
          existing.total.beforePromotionAndDiscount = passengerBeforePromo
            ? (parsePrice(existingBeforePromo) + parsePrice(passengerBeforePromo)).toString()
            : existingBeforePromo;

          existing.taxes = existing.taxes || { beforeDiscount: '0', afterDiscount: '0' };
          existing.taxes.beforeDiscount = (
            parsePrice(existing.taxes.beforeDiscount) + parsePrice(passenger.taxes?.beforeDiscount)
          ).toString();
          existing.taxes.afterDiscount = (
            parsePrice(existing.taxes.afterDiscount) + parsePrice(passenger.taxes?.afterDiscount)
          ).toString();
          existing.relatedItemCode = passenger.isApplicable ? obs.code : existing.relatedItemCode;
          existing.isApplicable = existing.isApplicable || passenger.isApplicable;
        }
      });
    });

    base.prices.perPassenger = Array.from(passengerMap.values());
    base.code = baseCard.code ?? base.code;
    base.deliveryDate = baseCard.deliveryDate ?? base.deliveryDate;

    return base;
  };

  const hasRelatedPackages = (card: OBSObjI): boolean => {
    return (
      (card.prepaidItems?.serviceCodes?.adults?.length ?? 0) > 0 ||
      (card.prepaidItems?.serviceCodes?.minors?.length ?? 0) > 0
    );
  };

  const getObsOverviewItems = (card: OBSObjI | null): OBSObjI[] => {
    if (!card) return [];
    const baseCode = card.code;
    if (!baseCode) return [card];

    const relatedCodesFromCart =
      cartItems?.filter((item) => item.relatedItemCode === baseCode).map((item) => item.code) ?? [];

    const codesToRender = [baseCode, ...relatedCodesFromCart];

    return codesToRender
      .map((code) => allOBS?.find((obs) => obs.code === code) ?? (code === baseCode ? card : null))
      .filter((item): item is OBSObjI => Boolean(item));
  };

  const handleMultipleShopRequest = (card: OBSObjI, viewItemData?: ViewItemRequestData) => {
    const existingCodes = allOBS?.map((obs) => obs.code ?? '') || [];

    getAllShopOBSData(card, existingCodes).then((allOBSResults) => {
      const mergedResult = mergeOBSResults(allOBSResults, card);
      dispatch(myAreaShopOBS(mergedResult));
      if (!booking) return;
      triggerViewItemEvent(
        card,
        booking,
        { ...viewItemData, shopResponse: mergedResult },
        cabinDetails,
        membershipData
      );
    });
  };

  const handleCardButtonClick = (
    cardData: CarouselCardData,
    productData: AllTypes,
    filteredPills: PillConfigurations[],
    itemCategory2?: string
  ) => {
    const viewItemData = {
      carousel_name: title,
      promoBadges: filteredPills,
      itemCategory2,
    };

    if (cardData.code) {
      dispatch(isLayerMyAreaExcursionOpen(true));
      dispatch(selectedMyAreaOBSCard(null));
      dispatch(selectedMyAreaExcursionCard(productData as MyMscExcursion));
      handleShopRequestExc(productData as MyMscExcursion, viewItemData);
    } else {
      dispatch(isLayerMyAreaExcursionOpen(false));
      dispatch(selectedMyAreaExcursionCard(null));
      dispatch(selectedMyAreaOBSCard(productData as OBSObjI));
      if (hasRelatedPackages(productData as OBSObjI)) {
        handleMultipleShopRequest(productData as OBSObjI, viewItemData);
      } else {
        handleShopRequestOBS(productData as OBSObjI, viewItemData);
      }
    }

    setSelectedCardPills(filteredPills);
    globalThis.getSelection()?.removeAllRanges();
    setIsDrawerOpen(true);
  };

  const validTypes = useMemo(
    () => productCarouselAEMProps.macroCategoryOrder.map((cat) => cat.type),
    [productCarouselAEMProps.macroCategoryOrder]
  );

  const filteredOBSItems = useMemo(
    () =>
      productCarouselData.obsData.filter((item) => {
        const typeCode = item.type.code;
        return (
          typeCode !== undefined &&
          validTypes.includes(typeCode) &&
          !isHiddenMinorObs(item, allOBS, allInclusiveCmsItems)
        );
      }),
    [productCarouselData.obsData, validTypes, allOBS, allInclusiveCmsItems]
  );

  const filteredExcursions = useMemo(
    () =>
      productCarouselData.excursionsData.filter(
        (exc) => Number(exc.inventory?.numberAvailable) != 0
      ),
    [productCarouselData.excursionsData]
  );

  const typeOrderMap = useMemo(
    () =>
      productCarouselAEMProps.macroCategoryOrder.reduce(
        (acc: { [key: string]: number }, cat, index) => {
          acc[cat.type] = index;
          return acc;
        },
        {}
      ),
    [productCarouselAEMProps.macroCategoryOrder]
  );

  const categoryDirectory = useMemo(
    () =>
      productCarouselAEMProps.macroCategoryOrder.reduce((acc: { [key: string]: string }, item) => {
        acc[item.type] = item.packageNameLabel;
        return acc;
      }, {}),
    [productCarouselAEMProps.macroCategoryOrder]
  );

  // mach obs codes from cms with API ones
  // use filtered obs

  const matchedOBSItemCodes = useMemo(() => {
    if (!serviceTypesItemCodes?.length) return {};

    return serviceTypesItemCodes
      .filter((serviceType) => serviceType.type !== 'EXCURSIONS')
      .reduce((acc, serviceType) => {
        acc[serviceType.type] = serviceType.serviceTypeCodes.filter((code) =>
          filteredOBSItems.some((item) => item.type.code === serviceType.type && item.code === code)
        );
        return acc;
      }, {} as Record<string, string[]>);
  }, [filteredOBSItems, serviceTypesItemCodes]);

  // mach excursion codes from cms with API ones
  // use filtered excursions
  const matchedExcursionItemCodes = useMemo(() => {
    if (!serviceTypesItemCodes) return [];
    const excursionServiceType = serviceTypesItemCodes.find(
      (serviceType) => serviceType.type === 'EXCURSIONS'
    );
    if (!excursionServiceType) return [];

    return filteredExcursions
      .filter((item) => excursionServiceType.serviceTypeCodes.includes(item.code))
      .map((item) => item.code)
      .filter((code): code is string => !!code);
  }, [filteredExcursions, serviceTypesItemCodes]);

  // new carousel data logic with round-robbin approach

  const carouselData = useMemo(() => {
    const MAX_ITEMS = carouseMaxItems;
    const usedCodes = new Set<string>();
    const carouseRotatedData: AllTypes[] = [];

    // Build per-service-type code pools
    // pools containing
    // {type: '', codes: [], fallback: []}
    const serviceTypePools = buildProductCarouPoolData(
      serviceTypesItemCodes ?? [],
      matchedExcursionItemCodes,
      matchedOBSItemCodes,
      productCarouselData,
      isLowestPriceSortingEnabled
    );

    // Round-robin: keep cycling until 6 items
    // order is defined by mached codes either for excursion or obs
    let round = 0;
    while (carouseRotatedData.length < MAX_ITEMS) {
      let addedInRound = false;

      for (const pool of serviceTypePools) {
        if (carouseRotatedData.length >= MAX_ITEMS) break;

        // Try to get code at current round index from matched codes
        const code = pool.itemCodes[round];

        if (code && !usedCodes.has(code)) {
          // Use the matched code for this round
          const item =
            pool.type === 'EXCURSIONS'
              ? productCarouselData.excursionsData.find((i) => i.code === code)
              : productCarouselData.obsData.find((i) => i.code === code);

          if (item) {
            carouseRotatedData.push(item);
            usedCodes.add(code);
            addedInRound = true;
          }
        } else if (pool.itemCodes[round] === undefined) {
          // Codes exhausted for this type, pick the first available from fallback
          const available = pool.fallbackCodes.filter((c) => !usedCodes.has(c));
          if (available.length > 0) {
            const firstAvailable = available[0];
            const item =
              pool.type === 'EXCURSIONS'
                ? productCarouselData.excursionsData.find((i) => i.code === firstAvailable)
                : productCarouselData.obsData.find((i) => i.code === firstAvailable);

            if (item) {
              carouseRotatedData.push(item);
              usedCodes.add(firstAvailable);
              addedInRound = true;
            }
          }
        }
      }

      round++;

      // Safety: if nothing was added in a full round, break to avoid infinite loop
      if (!addedInRound) break;
    }

    return carouseRotatedData;
  }, [serviceTypesItemCodes, matchedExcursionItemCodes, matchedOBSItemCodes, productCarouselData]);

  const filteredPillsMap = useMemo<Record<string, PillConfigurations[]>>(() => {
    const map: Record<string, PillConfigurations[]> = {};
    carouselData?.forEach((card) => {
      if (card.code) {
        map[card.code] = filterPills(
          card,
          cardPillConfigurations,
          myAreaPillsVisibility,
          undefined,
          booking,
          cartItems
        );
      }
    });
    return map;
  }, [carouselData, cardPillConfigurations, myAreaPillsVisibility, booking, cartItems]);

  useEffect(() => {
    if (
      cruiseExcursions !== undefined &&
      cruiseObs !== undefined &&
      carouselData &&
      carouselData.length > 2 &&
      booking &&
      cabinDetails
    ) {
      const indexedCarouselData = carouselData.map((data, index) => {
        const pills = filteredPillsMap[data.code ?? ''] ?? [];
        return {
          ...data,
          index: index + 1,
          promoPills: pills.map((p) => p.pillLabel?.toLowerCase().trim() ?? '').join(':'),
          itemCategory2:
            'type' in data && data?.type?.code
              ? alreadyPurchasedSectionRouteMapping
                  ?.find((routeItem) => Object.keys(routeItem.routeTypes).includes(data.type.code))
                  ?.routeLabel.toLowerCase()
              : '',
        };
      });
      const viewItemListDataKey = `${booking.number}:${title}:${indexedCarouselData
        .map((card) => card.code)
        .join(',')}`;
      const pageUrlChanged = viewItemListPageUrlRef.current !== currentPageUrl;
      const dataChanged = viewItemListDataKeyRef.current !== viewItemListDataKey;

      if (pageUrlChanged) {
        viewItemListPageUrlRef.current = currentPageUrl;
        hasTriggeredViewItemListRef.current = false;

        if (!dataChanged && currentPageUrl !== lastTriggeredViewItemListPageUrlRef.current) {
          return;
        }
      }

      if (hasTriggeredViewItemListRef.current) {
        viewItemListDataKeyRef.current = viewItemListDataKey;
        return;
      }

      productCarouselVisitCounter += 1;
      const indexedCarouselDataForTracking = indexedCarouselData.map((card) => ({
        ...card,
        analyticsListKey: `${viewItemListDataKey}:${productCarouselVisitCounter}`,
      }));

      triggerViewItemList(
        indexedCarouselDataForTracking,
        booking,
        { carousel_name: title },
        cabinDetails,
        membershipData
      );
      hasTriggeredViewItemListRef.current = true;
      viewItemListDataKeyRef.current = viewItemListDataKey;
      lastTriggeredViewItemListPageUrlRef.current = currentPageUrl;
    }
  }, [
    alreadyPurchasedSectionRouteMapping,
    booking,
    cabinDetails,
    carouselData,
    cruiseExcursions,
    cruiseObs,
    currentPageUrl,
    filteredPillsMap,
    title,
    membershipData,
  ]);

  useEffect(() => {
    const item = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
    if (passengersToAdd && triggerAddToCart && item) {
      let updatedCart: CartItem[] = [];
      const promoBadges = selectedCardPills
        .map((badge) => badge.pillLabel?.toLowerCase().trim() ?? '')
        .join(':');
      const itemCategory2 =
        !isLayerExcursionOpen && myAreaOBSCardData?.type?.code
          ? alreadyPurchasedSectionRouteMapping
              ?.find((routeItem) =>
                Object.keys(routeItem.routeTypes).includes(myAreaOBSCardData.type.code)
              )
              ?.routeLabel.toLowerCase()
          : '';
      const itemListName = defineItemListName(item, {
        itemCategory2,
        carouselName: title,
      });
      if (
        !isLayerExcursionOpen &&
        myAreaOBSCardData &&
        hasRelatedPackages(myAreaOBSCardData) &&
        shopOBS &&
        allOBS?.length
      ) {
        updatedCart = addAllIncItemAndRelatedItemToCart(
          shopOBS,
          allOBS,
          myAreaOBSCardData,
          promoBadges,
          itemListName
        );
      } else if (
        !isLayerExcursionOpen &&
        myAreaOBSCardData?.type?.code === OBSTypesEnum.RESTAURANTS
      ) {
        updatedCart = addDiningItemToCart(
          myAreaOBSCardData,
          passengersToAdd,
          diningSelectionToAdd ?? {
            entryPoint: DiningFlowEntryPoint.PRODUCT_CARD,
          },
          promoBadges,
          itemListName
        );
      } else {
        updatedCart = addToCart(item, passengersToAdd, undefined, {
          selectedLanguages,
          promoBadges,
          itemListName,
        });
      }

      dispatch(updateCartItems(updatedCart));
      setTriggerAddToCart(false);
      hasAddedToCartRef.current = true;
      setDiningSelectionToAdd(null);
      if (confirmationDrawerProps?.showConfirmationDrawer !== false) {
        setIsConfirmationDrawer(true);
      } else {
        resetDrawerState();
      }
    }
  }, [
    confirmationDrawerProps?.showConfirmationDrawer,
    diningSelectionToAdd,
    passengersToAdd,
    triggerAddToCart,
  ]);

  if (
    (Object.keys(typeOrderMap).some((key) => key !== 'EXCURSIONS') &&
      !('EXCURSIONS' in typeOrderMap) &&
      !!cruiseObs &&
      filteredOBSItems.length === 0) ||
    (Object.keys(typeOrderMap).filter((key) => key !== 'EXCURSIONS').length === 0 &&
      'EXCURSIONS' in typeOrderMap &&
      !!cruiseExcursions &&
      filteredExcursions.length === 0)
  )
    return null;

  //props for carousel since it's shared on skeleton and on results
  const sharedCarouselProps = {
    styleOption: '-circular-actions -medium-actions -light -actions-right',
    swiperOptions: {
      slidesPerView: 'auto' as const,
      spaceBetween: 16,
      loop: false,
      speed: 200,
      breakpointsBase: 'container' as const,
    },
    isProgressBar: true,
    showTitles: false,
    isFreeMode: true,
    showIndicator: { mobile: false, tablet: false, desktop: false },
    showInNavIndicator: { mobile: false, tablet: false, desktop: true },
  };

  const resetDrawerState = () => {
    // Drawer UI state is reset here, but request guards are intentionally kept
    // untouched so reopening the same item does not trigger a new INT135 call.
    setIsDrawerOpen(false);
    toggleDisplayOverlay(false);
    setIsDrawerProcessing(false);
    setPassengersToAdd(null);
    setDiningSelectionToAdd(null);
    setIsConfirmationDrawer(false);
    setSelectedLanguages({});
    dispatch(isLayerMyAreaExcursionOpen(false));
    dispatch(selectedMyAreaExcursionCard(null));
    dispatch(selectedMyAreaOBSCard(null));
    // dispatch the signal we added an item only if we are on cart page
    // so we fire the prices API but not on the normal overview carousel
    if (hasAddedToCartRef.current && carouselIsOnCartPage) {
      dispatch(cartProductCarouselAdded());
    }
    hasAddedToCartRef.current = false; // always reset, so a later plain close doesn't fire it
  };

  const closeDrawer = resetDrawerState;

  const isMyMscExcursion = (card: AllTypes): card is MyMscExcursion => {
    return 'activityDetails' in card;
  };

  const getCardButtonLabel = (productData: AllTypes, bookedPassengers: string[]) => {
    const isRestaurantsType =
      !isMyMscExcursion(productData) && productData.type?.code === OBSTypesEnum.RESTAURANTS;
    const isProductInCart =
      !isMyMscExcursion(productData) &&
      cartItems.some(
        (item) => item.code === productData.code || item.relatedItemCode === productData.code
      );

    if (
      isRestaurantsType &&
      myPlanButtonDiningLabel &&
      getDisplayButton(bookedPassengers, booking, activeCabinId) &&
      (bookedPassengers.length > 0 || isProductInCart)
    ) {
      return myPlanButtonDiningLabel;
    }

    if (getDisplayButton(bookedPassengers, booking, activeCabinId) && bookedPassengers.length > 0) {
      return myPlanAlreadyPurchasedButtonLabel || buttonLabel || myPlanButtonLabel;
    }

    return buttonLabel || myPlanButtonLabel;
  };

  const renderTooltip = (longDescription?: string): JSX.Element | null => {
    const tooltipContent = extractTooltipContent(longDescription);
    if (!tooltipContent) {
      return null;
    }

    return (
      <div className={styles['additional-notes-container']}>
        <Text
          text={drawerAEMConfig.tooltipLabel}
          styleOption={'body-default'}
          tagHtml={'span'}
        />
        <TooltipNew
          appliedCssClassNames="default-text"
          triggerAriaLabel={drawerAEMConfig.tooltipLabel}
          content={
            <Text
              text={tooltipContent}
              styleOption={'body-xsmall'}
            />
          }
          positionStyle="top"
        >
          <div className={styles['additional-notes-container__icon']}>
            <Icon
              icon={drawerAEMConfig.tooltipIcon}
              ariaHidden={true}
            />
          </div>
        </TooltipNew>
      </div>
    );
  };

  const getDrawerContent = (
    order: (
      | 'imageTitle'
      | 'passengers'
      | 'overview'
      | 'arrayOfExcursions'
      | 'additionalNotesTooltip'
    )[]
  ): (React.JSX.Element | null)[] => {
    const components = {
      arrayOfExcursions: (myAreaExcursionCardData?.isPackage && (
        <div className={'excursion-accordions-container'}>
          {myAreaExcursionCardData?.packageDetails?.map(
            (excursionItem: PackageDetails, index: number) => (
              //return an <Accordion /> component for each excursion
              <Accordion
                key={`excursion-${excursionItem.code}`}
                question={excursionItem?.description ?? ''}
                answer={''}
                closingIcon={'up'}
                isOpen={openIndex === `excursion-${index}`}
                onToggle={() => handleToggle(`excursion-${index}`)}
                openingIcon={'down'}
                autoClose={true}
                multi={true}
              >
                <>
                  <Overview
                    key={`excursion-overview-${index}-${excursionItem.code}`}
                    excursion={excursionItem}
                  />
                  <Text
                    key={`excursion-long-description-${index}-${excursionItem.code}`}
                    text={decodeHtmlEntities(excursionItem?.longDescription ?? '')}
                    tagHtml={'p'}
                    styleOption={'body-default'}
                  />
                </>
              </Accordion>
            )
          )}
        </div>
      )) || <></>,
      passengers: (
        <Passengers
          isDrawerOpen={isDrawerOpen}
          setPassengersToAdd={setPassengersToAdd}
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
          hidePassengersInCart
          onAllPassengersInCartChange={setAllPassengersInCart}
          isNoEligible={
            isLayerExcursionOpen
              ? isNoEligiblePassengersForExcursion(
                  myAreaExcursionCardData,
                  shopExcursion,
                  activeCabinId,
                  booking
                )
              : isNoEligiblePassengersForOBS(myAreaOBSCardData, shopOBS, activeCabinId, booking)
          }
          showGeneralHeadsUp
        />
      ),
      overview: <Overview excursion={isLayerExcursionOpen ? myAreaExcursionCardData : null} />,
      imageTitle: (
        <ImageTitle
          imageCode={
            (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ?? ''
          }
          booking={booking}
        />
      ),
      additionalNotesTooltip: renderTooltip(
        isLayerExcursionOpen
          ? myAreaExcursionCardData?.longDescription
          : myAreaOBSCardData?.longDescription
      ),
    };

    return order.map((key) => <React.Fragment key={key}>{components[key]}</React.Fragment>);
  };

  function titleSection(preTitle: string, title: string, subtitle: string) {
    return (
      <div className={'productCarouselContainer-headers'}>
        {preTitle && (
          <Text
            text={preTitle}
            tagHtml="p"
            styleOption="preTitle text spacer-mb-016 spacer-md-mb-024"
          />
        )}
        {title && (
          <Text
            text={title}
            tagHtml="p"
            styleOption="title text header-m spacer-mb-016 spacer-md-mb-008"
          />
        )}
        {subtitle && (
          <Text
            text={subtitle}
            tagHtml="p"
            styleOption="subtitle text"
          />
        )}
      </div>
    );
  }

  function skeleton() {
    return (
      <div className={'productCarouselContainer productCarouselContainer_skeleton'}>
        {titleSection(preTitle, title, subtitle)}
        <Carousel
          key="productcarousel-skeleton"
          id={'productcarousel-skeleton'}
          data={new Array(5).fill('')}
          showArrows={!isMobile}
          {...sharedCarouselProps}
          renderItem={() => {
            return <PostBookingCardSkeleton />;
          }}
        />
      </div>
    );
  }

  if (!carouselData || carouselData.length === 0) {
    return skeleton();
  }

  if (carouselData.length < 3) {
    return null;
  }

  return (
    <div className={'productCarouselContainer'}>
      {titleSection(preTitle, title, subtitle)}
      <Carousel
        key={dataComponentName}
        id={dataComponentName}
        data={carouselData}
        showArrows={!isMobile}
        {...sharedCarouselProps}
        renderItem={(productObject: object, index: number) => {
          const productData = { ...productObject, index: index + 1 } as AllTypes;

          let cardData: CarouselCardData;
          const bookedPassengers: string[] = booking
            ? getAlreadyBookedPassengerId(productData, booking, isMyMscExcursion(productData))
            : [];
          const filteredPills = filteredPillsMap[productData.code ?? ''] ?? [];
          const itemCategory2 =
            !isMyMscExcursion(productData) && productData.type?.code
              ? alreadyPurchasedSectionRouteMapping
                  ?.find((routeItem) =>
                    Object.keys(routeItem.routeTypes).includes(productData.type.code)
                  )
                  ?.routeLabel.toLowerCase()
              : '';
          if ('type' in productData) {
            const { price, priceLabel } = getPriceFromPriceBasis(
              productData,
              perCabinLabel,
              perAdultLabel,
              perChildLabel
            );
            cardData = {
              image:
                getDynamicMediaPath(productData.code) ||
                `${getObsBaseImagePath()}/${productData.code}.jpg`,
              imagePlaceholder: getObsPlaceholderImagePath(),
              imageCode: productData.code,
              imageType: 'obs' as const,
              text: productData.type.code ? categoryDirectory[productData.type.code] : '',
              packageTitle: productData.description,
              price: price,
              priceLabel: priceLabel,
              bookedPassengers: booking
                ? getNamesOfAlreadyBookedPassengers(bookedPassengers, booking).join(', ')
                : '',
              code: '',
              isAllIncItem:
                Boolean(productData.prepaidItems?.isAllInclusive) ||
                productData.type.code === OBSTypesEnum.BEVERAGES.toString(),
            };
          } else if ('code' in productData) {
            const { price, priceLabel } = getPriceFromPriceBasis(
              productData,
              perPackageLabel,
              perAdultLabel,
              perChildLabel
            );
            cardData = {
              image:
                getDynamicMediaPath(productData.code) ||
                `${getActBaseImagePath()}/${productData.code}.jpg`,
              imagePlaceholder: getActPlaceholderImagePath(),
              imageCode: productData.code,
              imageType: 'act' as const,
              text: `${dayLabel}`,
              day: productData.day,
              portCode: productData.portCode,
              startDate: productData.startDate,
              dayAtSeaLabelValue: dayAtSeaLabelValue,
              packageTitle: productData.description,
              durationLabel: replacePlaceholders(
                managePlurals(
                  productData.duration,
                  propertiesFromPlanMyCruiseContainer.durationValueLabel
                ),
                {
                  duration: productData.duration.toString(),
                }
              ),
              price: price,
              priceLabel: priceLabel,
              bookedPassengers: booking
                ? getNamesOfAlreadyBookedPassengers(bookedPassengers, booking).join(', ')
                : '',
              code: productData.code,
            };
          }

          const obsProductData = productData as OBSObjI;
          const isPackageType =
            obsProductData.prepaidItems?.isPerCouple ||
            obsProductData.type?.code === OBSTypesEnum.PHOTO ||
            obsProductData.type?.code === OBSTypesEnum.LAUNDR;

          const bookedPassengersAllCabins = getAlreadyBookedPassengerId(
            cardData,
            booking,
            isMyMscExcursion(productData),
            true
          );

          return (
            cardData && (
              <PostBookingCard
                key={`productcarousel-card-${productData.code}-${productData.index}`}
                id={String(index)}
                image={cardData.image}
                title={cardData?.packageTitle ?? ''}
                imageCode={cardData.imageCode}
                imageType={cardData.imageType}
                displayedPills={filteredPills}
                placeholderImagePath={cardData.imagePlaceholder}
                clickDisabled={
                  !isReservationAvailable(
                    cardData,
                    isMyMscExcursion(productData),
                    booking,
                    activeCabinId
                  ) || !getDisplayButton(bookedPassengersAllCabins, booking, activeCabinId)
                }
              >
                {cardData.day &&
                cardData.portCode &&
                cardData.startDate &&
                cardData.dayAtSeaLabelValue ? (
                  <PostBookingCard.PreTitle
                    text={cardData.text}
                    day={`${cardData.day}`}
                    portCode={cardData.portCode}
                    startDate={cardData.startDate}
                    dayAtSeaLabelValue={cardData.dayAtSeaLabelValue}
                  />
                ) : (
                  <PostBookingCard.PreTitle text={cardData?.text ?? ''} />
                )}
                <PostBookingCard.Title packageTitle={cardData?.packageTitle ?? ''} />
                <PostBookingCard.Description
                  descriptionItems={[
                    {
                      descriptionLabel: cardData?.durationLabel ? durationLabel : '',
                      descriptionValue: cardData?.durationLabel
                        ? String(cardData?.durationLabel)
                        : '',
                    },
                    { descriptionLabel: codeLabel, descriptionValue: cardData.code },
                    {
                      descriptionLabel:
                        !cardData.isAllIncItem && cardData.bookedPassengers.length > 0
                          ? alreadyBookedLabel
                          : '',
                      descriptionValue:
                        !cardData.isAllIncItem && cardData.bookedPassengers.length > 0
                          ? cardData.bookedPassengers
                          : '',
                    },
                  ]}
                />
                {isPriceTypeNotAllowed(booking) ? (
                  <Button
                    text={myPlanButtonPurchaseDisabledLabel}
                    styleOption={'cmp-button__secondary purchase-disabled'}
                    onClick={() =>
                      handleCardButtonClick(cardData, productData, filteredPills, itemCategory2)
                    }
                  />
                ) : (
                  <PostBookingCard.PriceSection
                    prices={
                      (booking &&
                        !isReservationAvailable(
                          cardData,
                          isMyMscExcursion(productData),
                          booking,
                          activeCabinId
                        )) ||
                      !isItemNotFullyBooked(bookedPassengersAllCabins, booking, activeCabinId)
                        ? []
                        : getPostBookingCardPrices({
                            card: productData,
                            promotionPriceEnabled,
                            showSinglePromotionPrice: isPackageType || showSinglePromotionPrice,
                            perCabinLabel: isPackageType ? perPackageLabel : perCabinLabel,
                            perAdultLabel: isPackageType ? perPackageLabel : perAdultLabel,
                            perChildLabel: isPackageType ? perPackageLabel : perChildLabel,
                            linkedMinorCard: getLinkedMinorObs(
                              productData as OBSObjI,
                              allOBS,
                              allInclusiveCmsItems
                            ),
                          })
                    }
                    buttonLabel={getCardButtonLabel(productData, bookedPassengers)}
                    buttonAriaLabel={postBookingButtonAriaLabel}
                    handleButtonClick={() =>
                      handleCardButtonClick(cardData, productData, filteredPills, itemCategory2)
                    }
                    displayButton={
                      getDisplayButton(bookedPassengersAllCabins, booking, activeCabinId) &&
                      isReservationAvailable(
                        cardData,
                        isMyMscExcursion(productData),
                        booking,
                        activeCabinId
                      )
                    }
                  />
                )}
              </PostBookingCard>
            )
          );
        }}
      />
      {!isConfirmationDrawer &&
        (myAreaOBSCardData?.type?.code === OBSTypesEnum.RESTAURANTS ? (
          <DiningMultistepDrawer
            isDrawerOpen={isDrawerOpen}
            onClose={closeDrawer}
            booking={booking}
            setIsConfirmationDrawer={() => setIsConfirmationDrawer(true)}
            flowConfig={{ entryPoint: DiningFlowEntryPoint.PRODUCT_CARD }}
            preserveSelectionGoingBack={true}
            onConfirmPassengers={(passengers, _currentStep, diningSelection) => {
              setPassengersToAdd(passengers);
              setDiningSelectionToAdd(diningSelection ?? null);
            }}
            onApply={() => {
              setTriggerAddToCart(true);
              setIsDrawerProcessing(true);
              const activeCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
              triggerAddToCartEvent(
                activeCard,
                booking,
                cabinDetails,
                getCurrencyCode(),
                getAvailablePrice(drawerPrice),
                {
                  promoBadges: selectedCardPills,
                  itemCategory2:
                    !isLayerExcursionOpen && activeCard.type?.code
                      ? alreadyPurchasedSectionRouteMapping
                          ?.find((routeItem) =>
                            Object.keys(routeItem.routeTypes).includes(activeCard.type.code)
                          )
                          ?.routeLabel.toLowerCase()
                      : '',
                  carouselName: title,
                  passengersToAdd,
                },
                membershipData
              );
            }}
          />
        ) : (
          <Drawer
            closeIcon={drawerAEMConfig.closeDrawerIcon}
            firstButtonLabel={drawerExcursionAEMConfig?.addToCartButtonLabel}
            limitedSeatsPlaceholder={drawerAEMConfig.limitedSeatsPlaceholder}
            secondButtonLabel=""
            alreadyInCartMessageLabel={
              allPassengersInCart ? drawerAEMConfig.alreadyInCartMessageLabel : ''
            }
            alreadyInCartCtaLabel={allPassengersInCart ? drawerAEMConfig.alreadyInCartCtaLabel : ''}
            alreadyInCartCtaLink={
              allPassengersInCart ? drawerAEMConfig.alreadyInCartCtaLink : undefined
            }
            processingFirstButtonLabel={drawerExcursionAEMConfig?.addingToCartButtonLabel}
            processingFirstButtonIcon={drawerExcursionAEMConfig?.addingToCartButtonIcon}
            isDrawerOpen={isDrawerOpen}
            enableClosingByClickingOutside={true}
            onClose={closeDrawer}
            onApply={() => {
              const isAllIncOrCombinedCard =
                !isLayerExcursionOpen && !!myAreaOBSCardData?.prepaidItems?.isAllInclusive;

              if (
                isAllIncOrCombinedCard &&
                (bookingHasAllInclusiveOrCombined || cartHasAllInclusiveOrCombined)
              ) {
                toggleDisplayOverlay(false);
                setIsDrawerProcessing(false);
                setIsAllInclusiveInCartErrorModalOpen(true);
                return;
              }

              toggleDisplayOverlay(false);
              setTriggerAddToCart(true);
              setIsDrawerProcessing(true);
              const activeCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
              triggerAddToCartEvent(
                activeCard,
                booking,
                cabinDetails,
                getCurrencyCode(),
                getAvailablePrice(drawerPrice),
                {
                  promoBadges: selectedCardPills,
                  itemCategory2:
                    !isLayerExcursionOpen && activeCard.type?.code
                      ? alreadyPurchasedSectionRouteMapping
                          ?.find((routeItem) =>
                            Object.keys(routeItem.routeTypes).includes(activeCard.type.code)
                          )
                          ?.routeLabel.toLowerCase()
                      : '',
                  carouselName: title,
                  passengersToAdd,
                },
                membershipData
              );
            }}
            stylingOption="transparent-header"
            isProcessing={isDrawerProcessing}
            disabled={!canAddToCartInPostBooking}
            hiddenFooter={
              isPriceTypeNotAllowed(booking) ||
              (isLayerExcursionOpen
                ? isNoEligiblePassengersForExcursion(
                    myAreaExcursionCardData,
                    shopExcursion,
                    activeCabinId,
                    booking
                  )
                : isNoEligiblePassengersForOBS(myAreaOBSCardData, shopOBS, activeCabinId, booking))
            }
          >
            {isLayerExcursionOpen
              ? getDrawerContent([
                  'imageTitle',
                  'overview',
                  'passengers',
                  'arrayOfExcursions',
                  ...(!drawerAEMConfig?.disableAdditionalNotesTooltip
                    ? (['additionalNotesTooltip'] as const)
                    : []),
                ])
              : getDrawerContent([
                  'imageTitle',
                  'passengers',
                  ...(!drawerAEMConfig?.disableAdditionalNotesTooltip
                    ? (['additionalNotesTooltip'] as const)
                    : []),
                ])}
          </Drawer>
        ))}
      {isConfirmationDrawer && (
        <Drawer
          closeIcon={drawerAEMConfig.closeDrawerIcon}
          title={confirmationDrawerProps?.title}
          titleIcon={confirmationDrawerProps?.titleIcon}
          firstButtonLabel={confirmationDrawerProps?.continueShoppingButtonLabel}
          secondButtonLabel={confirmationDrawerProps?.viewCartButtonLabel}
          limitedSeatsPlaceholder={drawerAEMConfig.limitedSeatsPlaceholder}
          isDrawerOpen={isDrawerOpen}
          enableClosingByClickingOutside={true}
          onClose={resetDrawerState}
          onClear={() => {
            resetDrawerState();
            navigateOrRedirectToRoute(confirmationDrawerProps?.viewCartLink || {}, navigate);
          }}
          onApply={resetDrawerState}
          stylingOption="confirmation-drawer"
          footerButtonsVariant={'secondary'}
        >
          <div className="add-to-cart-overview-list">
            {isLayerExcursionOpen ? (
              <AddToCartOverview
                postBookingCard={myAreaExcursionCardData}
                passengersToAdd={passengersToAdd}
              />
            ) : (
              (() => {
                // Combined adult + minor ALLINC packages render as a single merged card.
                const [primaryItem, ...relatedItems] = getObsOverviewItems(myAreaOBSCardData);
                return primaryItem ? (
                  <AddToCartOverview
                    postBookingCard={primaryItem}
                    relatedCards={relatedItems}
                    passengersToAdd={passengersToAdd}
                  />
                ) : null;
              })()
            )}
          </div>
        </Drawer>
      )}
      {isAllInclusiveInCartErrorModalOpen && shouldRenderAllInclusiveInCartErrorModal && (
        <ErrorModal
          title={allInclusiveInCartErrorTitle ?? ''}
          message={allInclusiveInCartErrorDescription ?? ''}
          styleOption="-all-inclusive-in-cart-modal"
          ctaLabel={allInclusiveInCartErrorButtonText ?? ''}
          handleClosure={() => setIsAllInclusiveInCartErrorModalOpen(false)}
          handleClick={() => setIsAllInclusiveInCartErrorModalOpen(false)}
        />
      )}
    </div>
  );
}

export default ProductCarousel;
